import { useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { comprimirImagemParaDataUrl } from '@/utils/comprimir-imagem';
import { useI18n } from '@/contexts/i18n-hook';

interface Props {
  apreciacaoId: string;
  onPartesGeradas: () => Promise<void> | void;
}

type Fase = 'idle' | 'lendo' | 'descrevendo' | 'agrupando' | 'detalhando' | 'concluido' | 'erro';

interface ImagemLocal {
  base64: string;
  fileName: string;
  preview: string;
}

// Comprime antes de virar base64: a imagem viaja inteira no corpo da requisição para a IA.
function fileToBase64(file: File): Promise<string> {
  return comprimirImagemParaDataUrl(file);
}

export function AIGeneratePartesPanel({ apreciacaoId, onPartesGeradas }: Props) {
  const { t } = useI18n();
  const [imagens, setImagens] = useState<ImagemLocal[]>([]);
  const [fase, setFase] = useState<Fase>('idle');
  const [erro, setErro] = useState<string | null>(null);
  const [resumo, setResumo] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const adicionarArquivos = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    setFase('lendo');
    const novos: ImagemLocal[] = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      const base64 = await fileToBase64(file);
      novos.push({ base64, fileName: file.name, preview: base64 });
    }
    setImagens((prev) => [...prev, ...novos]);
    setFase('idle');
  };

  const removerImagem = (index: number) => {
    setImagens((prev) => prev.filter((_, i) => i !== index));
  };

  const limpar = () => {
    setImagens([]);
    setErro(null);
    setResumo(null);
    setFase('idle');
  };

  const [progresso, setProgresso] = useState<string>('');

  const gerar = async () => {
    if (imagens.length === 0) return;
    setErro(null);
    setResumo(null);
    setProgresso('');
    setFase('descrevendo');
    try {
      // Passo A — descrever em LOTES de 5 imagens (1 request por lote), com
      // concorrência limitada de lotes. Menos requisições e bem mais rápido.
      const descricoes: Array<{ rotulo: string; descricao: string }> = new Array(imagens.length);
      const urls: string[] = new Array(imagens.length);
      const TAMANHO_LOTE = 5;
      const LIMITE_LOTES = 3;
      // Cada lote guarda os índices GLOBAIS das imagens, para remontar na ordem.
      const lotes: number[][] = [];
      for (let i = 0; i < imagens.length; i += TAMANHO_LOTE) {
        lotes.push(Array.from({ length: Math.min(TAMANHO_LOTE, imagens.length - i) }, (_, j) => i + j));
      }
      let cursor = 0;
      let feitas = 0;
      setProgresso(t('apreciacoes.editar.ia.progressoImagens', { feitas: 0, total: imagens.length }));
      const workers = Array.from({ length: Math.min(LIMITE_LOTES, lotes.length) }, async () => {
        while (true) {
          const loteIdx = cursor++;
          if (loteIdx >= lotes.length) return;
          const indices = lotes[loteIdx];
          const payload = indices.map((i) => ({ base64: imagens[i].base64, fileName: imagens[i].fileName }));
          const resp = await apiService.aiDescreverImagens(apreciacaoId, payload);
          if (!resp.success || !resp.data)
            throw new Error(
              resp.message || t('apreciacoes.editar.ia.falhaLote', { lote: loteIdx + 1 })
            );
          indices.forEach((gi, k) => {
            const d = resp.data![k];
            descricoes[gi] = { rotulo: d?.rotulo ?? `Imagem ${gi + 1}`, descricao: d?.descricao ?? '' };
            urls[gi] = d?.url ?? '';
          });
          feitas += indices.length;
          setProgresso(t('apreciacoes.editar.ia.progressoImagens', { feitas, total: imagens.length }));
        }
      });
      await Promise.all(workers);

      // Passo B — agrupar
      setFase('agrupando');
      setProgresso('');
      const respAgrup = await apiService.aiAgruparPartes(apreciacaoId, descricoes);
      if (!respAgrup.success || !respAgrup.data)
        throw new Error(respAgrup.message || t('apreciacoes.editar.ia.falhaAgrupar'));
      const grupos = respAgrup.data.grupos;

      // Passo C — detalhar cada parte (sequencial, persistindo uma de cada vez)
      setFase('detalhando');
      let criadas = 0;
      const erros: string[] = [];
      setProgresso(t('apreciacoes.editar.ia.progressoPartes', { feitas: 0, total: grupos.length }));
      for (let idx = 0; idx < grupos.length; idx++) {
        const grupo = grupos[idx];
        // Se a parte não tem imagens associadas, enviar todas para a IA procurar
        const indices = grupo.imagensIndices.length > 0
          ? grupo.imagensIndices
          : imagens.map((_, i) => i);
        const imagensBase64 = indices.map((i) => imagens[i].base64);
        const urlsParte = indices.slice(0, 4).map((i) => urls[i]);
        // Passar as descrições do Passo A para salvar no risco
        const descricoesParte = indices.map((i) => `${descricoes[i].rotulo}: ${descricoes[i].descricao}`);
        try {
          const resp = await apiService.aiDetalharParte(apreciacaoId, {
            parteNome: grupo.parteNome,
            imagensBase64,
            urls: urlsParte,
            descricoes: descricoesParte,
          });
          if (resp.success) criadas++;
          else erros.push(`${grupo.parteNome}: ${resp.message}`);
        } catch (e: any) {
          erros.push(`${grupo.parteNome}: ${e?.message || 'erro'}`);
        }
        setProgresso(t('apreciacoes.editar.ia.progressoPartes', { feitas: idx + 1, total: grupos.length }));
        // Atualiza a lista na tela a cada parte concluída
        await onPartesGeradas();
      }

      setResumo(
        erros.length > 0
          ? t('apreciacoes.editar.ia.resumoComErros', {
              criadas,
              grupos: grupos.length,
              erros: erros.length,
            })
          : t('apreciacoes.editar.ia.resumo', { criadas, grupos: grupos.length })
      );
      setFase('concluido');
      setImagens([]);
    } catch (e: any) {
      setErro(e?.message || t('apreciacoes.editar.ia.erroGerar'));
      setFase('erro');
    }
  };

  const ocupado = fase === 'descrevendo' || fase === 'agrupando' || fase === 'detalhando' || fase === 'lendo';

  return (
    <div className="rounded-lg border-2 border-dashed border-purple-400 dark:border-purple-600 bg-purple-50/50 dark:bg-purple-950/20 p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-purple-900 dark:text-purple-200">
            {t('apreciacoes.editar.ia.titulo')}
          </h3>
          <p className="text-sm text-purple-800 dark:text-purple-300 mt-1">
            {t('apreciacoes.editar.ia.subtitulo')}
          </p>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          adicionarArquivos(e.dataTransfer.files);
        }}
        onPaste={(e) => {
          const files = Array.from(e.clipboardData?.files || []);
          if (files.length > 0) adicionarArquivos(files);
        }}
        className="rounded-lg border-2 border-dashed border-purple-300 dark:border-purple-700 p-6 bg-white dark:bg-schmersal-gray-darkest text-center"
      >
        <p className="text-sm text-schmersal-gray-dark dark:text-white">
          {(() => {
            const [antes, depois] = t('apreciacoes.editar.ia.dropzone').split('{{acao}}');
            return (
              <>
                {antes}
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="text-purple-700 dark:text-purple-300 underline"
                >
                  {t('apreciacoes.editar.ia.selecioneArquivos')}
                </button>
                {depois}
              </>
            );
          })()}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => adicionarArquivos(e.target.files)}
        />
      </div>

      {imagens.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {imagens.map((img, i) => (
            <div key={i} className="relative group">
              <img
                src={img.preview}
                alt={img.fileName}
                className="w-full h-24 object-cover rounded border border-schmersal-gray-light dark:border-schmersal-gray-dark"
              />
              <button
                type="button"
                onClick={() => removerImagem(i)}
                disabled={ocupado}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 text-xs opacity-0 group-hover:opacity-100 disabled:opacity-0"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
          {imagens.length > 0 && t('apreciacoes.editar.ia.prontas', { total: imagens.length })}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={limpar}
            disabled={ocupado || imagens.length === 0}
            className="px-4 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark
                       text-schmersal-gray-dark dark:text-white disabled:opacity-60"
          >
            {t('comum.acao.limpar')}
          </button>
          <button
            type="button"
            onClick={gerar}
            disabled={ocupado || imagens.length === 0}
            className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold disabled:opacity-60"
          >
            {ocupado ? t('apreciacoes.editar.ia.processando') : t('apreciacoes.editar.ia.gerar')}
          </button>
        </div>
      </div>

      {(ocupado || fase === 'concluido' || fase === 'erro') && (
        <div className="text-sm space-y-1">
          <PassoStatus label={t('apreciacoes.editar.ia.passoDescrever')} ativo={fase === 'descrevendo'} concluido={['agrupando', 'detalhando', 'concluido'].includes(fase)} />
          <PassoStatus label={t('apreciacoes.editar.ia.passoAgrupar')} ativo={fase === 'agrupando'} concluido={['detalhando', 'concluido'].includes(fase)} />
          <PassoStatus label={t('apreciacoes.editar.ia.passoDetalhar')} ativo={fase === 'detalhando'} concluido={fase === 'concluido'} />
          {progresso && <div className="pl-6 text-xs text-purple-800 dark:text-purple-300">{progresso}</div>}
        </div>
      )}

      {resumo && <p className="text-sm text-green-700 dark:text-green-400">{resumo}</p>}
      {erro && <p className="text-sm text-red-600">{erro}</p>}
    </div>
  );
}

function PassoStatus({ label, ativo, concluido }: { label: string; ativo: boolean; concluido: boolean }) {
  const icone = concluido ? '✓' : ativo ? '⟳' : '•';
  const cor = concluido ? 'text-green-600' : ativo ? 'text-purple-700 font-semibold' : 'text-schmersal-gray-medium';
  return (
    <div className={`flex items-center gap-2 ${cor}`}>
      <span className={ativo ? 'animate-spin inline-block' : ''}>{icone}</span>
      <span>{label}</span>
    </div>
  );
}
